import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Button,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiHelpCircleOutline, mdiPencilOutline, mdiCheck } from "@mdi/js";
import { ArrowLeft } from "react-bootstrap-icons";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { reorderItems } from "../../../utils/dndHelpers";
import {
  fetchTemplateDetails,
  clearTemplateDetails,
  setTemplateAsDefault,
  clearSetDefaultStatus,
  deleteTemplate,
  clearDeleteStatus,
} from "../slice";
import { useApp } from "../../../contexts/AppContext";
import LoadingSpinner from "../../../components/LoadingSpinner";
import CustomToast from "../../../components/common/CustomToast";
import SectionCard from "./SectionCard";
import RenameModal from "./RenameModal";
import EditSectionsModal from "./EditSectionsModal";
import EditHowItWorksModal from "./EditHowItWorksModal";
import DeleteTemplateConfirmationModal from "../DeleteTemplateConfirmationModal";
import UpgradePlanModal from "../../../components/WebApp/GlobalNavBar/UpgradePlanModal";
import "./templateDetails.css";

const TemplateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    user: { profile },
  } = useApp();

  // Redux state
  const {
    templateDetails,
    fetchTemplateDetailsStatus,
    fetchTemplateDetailsError,
    setDefaultStatus,
    setDefaultError,
    deleteTemplateStatus,
    deleteTemplateError,
  } = useSelector((state) => state.templates);

  // Local state
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showEditSectionsModal, setShowEditSectionsModal] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [sections, setSections] = useState([]);
  const hasInitialized = useRef(false);

  // Fetch template details on mount
  useEffect(() => {
    if (id && !hasInitialized.current) {
      hasInitialized.current = true;
      dispatch(fetchTemplateDetails(id));
    }
    return () => {
      dispatch(clearTemplateDetails());
    };
  }, [id, dispatch]);

  // Update local sections when templateDetails changes
  useEffect(() => {
    if (templateDetails?.current_version?.sections?.sections) {
      // Transform API response to include IDs and order
      const transformedSections =
        templateDetails.current_version.sections.sections.map(
          (section, sectionIndex) => ({
            id: `section-${sectionIndex}`,
            name: section.name,
            order: sectionIndex + 1,
            collapsed: false,
            subsections: section.subsections
              ? section.subsections.map((subsection, subIndex) => ({
                  id: `sub-${sectionIndex}-${subIndex}`,
                  name: subsection.name,
                  order: subIndex + 1,
                }))
              : [],
          })
        );
      setSections(transformedSections);
    }
  }, [templateDetails]);

  // Handle set default status changes
  useEffect(() => {
    if (setDefaultStatus === "success") {
      setSuccessMessage(
        `${
          templateDetails?.name || "Template"
        } is now your default section template.`
      );
      setShowSuccessToast(true);
      dispatch(clearSetDefaultStatus());
    } else if (setDefaultStatus === "failed") {
      setErrorMessage(
        setDefaultError ||
          "Failed to set template as default. Please try again."
      );
      setShowErrorToast(true);
      dispatch(clearSetDefaultStatus());
    }
  }, [setDefaultStatus, setDefaultError, templateDetails, dispatch]);

  // Handle delete status changes
  useEffect(() => {
    if (deleteTemplateStatus === "success") {
      setSuccessMessage("Section Template deleted successfully.");
      setShowSuccessToast(true);
      setShowDeleteModal(false);
      dispatch(clearDeleteStatus());
      // Navigate back to templates list after successful deletion with a small delay to show toast
      setTimeout(() => {
        navigate("/templates");
      }, 1500);
    } else if (deleteTemplateStatus === "failed") {
      setErrorMessage(
        deleteTemplateError || "Failed to delete template. Please try again."
      );
      setShowErrorToast(true);
      dispatch(clearDeleteStatus());
    }
  }, [deleteTemplateStatus, deleteTemplateError, dispatch, navigate]);

  // Handlers
  const handleBack = () => {
    navigate("/templates");
  };

  const handleRename = () => {
    setShowRenameModal(true);
  };

  const handleEdit = () => {
    setShowEditSectionsModal(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (templateDetails?.id) {
      dispatch(deleteTemplate(templateDetails.id));
    }
  };

  const handleSetAsDefault = () => {
    if (templateDetails?.id) {
      // Check if user is on free tier
      if (profile?.subscription?.tier === "Free") {
        setShowUpgradeModal(true);
        return;
      }

      dispatch(setTemplateAsDefault(templateDetails.id));
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSections((items) => reorderItems(items, active.id, over.id));
    }
  };

  const handleCollapseToggle = (sectionId) => {
    setSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId
          ? { ...section, collapsed: !section.collapsed }
          : section
      )
    );
  };

  // Loading state
  if (fetchTemplateDetailsStatus === "loading" || !templateDetails) {
    return <LoadingSpinner />;
  }

  // Error state
  if (fetchTemplateDetailsStatus === "failed") {
    return (
      <Container fluid className="template-details-container py-4">
        <div className="error-state">
          <h3>Error loading template</h3>
          <p>{fetchTemplateDetailsError}</p>
          <Button variant="primary" onClick={handleBack}>
            Back to Templates
          </Button>
        </div>
      </Container>
    );
  }

  const isSystemTemplate = templateDetails.is_system_default;
  const isDefaultTemplate = templateDetails.is_user_default;

  return (
    <Container fluid className="template-details-container py-4">
      {/* Header */}
      <div className="template-details-header">
        <div className="header-left">
          <Button
            variant="link"
            onClick={handleBack}
            className="p-0 text-decoration-none back-link"
          >
            <ArrowLeft size={14} className="me-2" />
            Back to Templates
          </Button>
        </div>
        <div className="header-right"></div>
      </div>

      {/* Template Info */}
      <div className="template-info">
        <div className="template-title">
          <div className="title-row">
            <h1>
              {templateDetails.name}
              {!isSystemTemplate && (
                <Button
                  variant="link"
                  className="edit-name-btn"
                  onClick={handleRename}
                >
                  <Icon path={mdiPencilOutline} size={1} color="#1C1B1F" />
                </Button>
              )}
            </h1>
            <div className="d-flex gap-2">
              {!isSystemTemplate && (
                <Button variant="outline-danger" onClick={handleDelete}>
                  Delete
                </Button>
              )}
              {isDefaultTemplate ? (
                <OverlayTrigger
                  placement="bottom"
                  trigger={["hover", "focus"]}
                  overlay={
                    <Tooltip id="default-template-tooltip">
                      Used automatically when you starting a new report.
                    </Tooltip>
                  }
                >
                  <span>
                    <Button
                      variant="primary"
                      disabled={true}
                      style={{ pointerEvents: "none" }}
                    >
                      <Icon path={mdiCheck} size={1} /> Default Template
                    </Button>
                  </span>
                </OverlayTrigger>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSetAsDefault}
                  disabled={
                    setDefaultStatus === "loading" ||
                    (templateDetails.is_system_default &&
                      templateDetails.is_user_default)
                  }
                  className="set-default-btn"
                >
                  {setDefaultStatus === "loading"
                    ? "Setting..."
                    : "Set as Default"}
                </Button>
              )}
            </div>
          </div>
          <div className="template-badges">
            {templateDetails.is_user_default && (
              <Badge bg="primary">Default</Badge>
            )}
            {isSystemTemplate && <Badge bg="secondary">System Built-in</Badge>}
          </div>
        </div>
        {!isSystemTemplate && (
          <div className="template-metadata">
            <div className="metadata-item">
              <span className="label">Created:</span>
              <span className="value">
                {templateDetails.created_at
                  ? new Date(templateDetails.created_at).toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "short", day: "numeric" }
                    )
                  : "-"}
              </span>
            </div>
            <div className="metadata-item">
              <span className="label">Last Modified:</span>
              <span className="value">
                {templateDetails.updated_at
                  ? new Date(templateDetails.updated_at).toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "short", day: "numeric" }
                    )
                  : "-"}
              </span>
            </div>
            <div className="metadata-item">
              <span className="label">Upload File:</span>
              <span className="value">
                {templateDetails.description.replace(
                  "Sections imported from",
                  ""
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="sections-container">
        <div className="sections-header">
          <h2>
            Sections
            <Icon
              path={mdiHelpCircleOutline}
              size={0.8}
              className="ms-2 text-muted"
              style={{ cursor: "pointer" }}
              onClick={() => setShowHowItWorksModal(true)}
            />
          </h2>
          {!isSystemTemplate && (
            <Button variant="outline-primary" onClick={handleEdit}>
              Edit
            </Button>
          )}
        </div>

        <div className="sections-content">
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections}
              strategy={verticalListSortingStrategy}
            >
              {sections.map((section) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  onCollapseToggle={handleCollapseToggle}
                  readOnly={true}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {/* Rename Modal */}
      <RenameModal
        show={showRenameModal}
        onHide={() => setShowRenameModal(false)}
        templateId={templateDetails.id}
        currentName={templateDetails.name}
        onSuccess={(message) => {
          setSuccessMessage(message);
          setShowSuccessToast(true);
        }}
        onError={(message) => {
          setErrorMessage(message);
          setShowErrorToast(true);
        }}
      />

      {/* Edit Sections Modal */}
      <EditSectionsModal
        show={showEditSectionsModal}
        onHide={() => setShowEditSectionsModal(false)}
        templateId={templateDetails.id}
        sections={sections}
        onSuccess={(message) => {
          setSuccessMessage(message);
          setShowSuccessToast(true);
        }}
        onError={(message) => {
          setErrorMessage(message);
          setShowErrorToast(true);
        }}
      />

      {/* How It Works Modal */}
      <EditHowItWorksModal
        show={showHowItWorksModal}
        onHide={() => setShowHowItWorksModal(false)}
      />

      {/* Success Toast */}
      <CustomToast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={successMessage}
        variant="success"
        position="bottom-end"
        autohide={true}
      />

      {/* Error Toast */}
      <CustomToast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        message={errorMessage}
        variant="error"
        position="bottom-end"
        autohide={true}
      />

      {/* Delete Template Confirmation Modal */}
      <DeleteTemplateConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={deleteTemplateStatus === "loading"}
      />

      {/* Upgrade Plan Modal */}
      <UpgradePlanModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </Container>
  );
};

export default TemplateDetails;
